import { useEffect, useState } from "react";
import {
  Aperture,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  LoaderCircle,
  LockKeyhole,
  MapPin,
  Sparkles
} from "lucide-react";
import {
  loadInquiryEventTypes,
  loadPublicPlans,
  reconcileInquiryPlanCheckout,
  requestOtp,
  startInquiryPlanCheckout,
  submitEventInquiry,
  verifyOtp
} from "./api";
import {
  eventLocalToUtc
} from "./inquiry-timezone";
import { getPanelUrls } from "./public-auth-routing";
import { showDevOtpFill } from "./otp-fill";
import "./public-experience.css";

const emptyForm = {
  clientName: "",
  email: "",
  phone: "",
  eventTypeId: "",
  eventName: "",
  startsAt: "",
  endsAt: "",
  city: "",
  country: "Canada",
  timezone: "America/Toronto",
  functionCount: "1",
  venueName: "",
  guestCount: "",
  services: ["photography"],
  planCode: "",
  message: ""
};

const services = [
  ["photography", "Photography"],
  ["videography", "Videography"],
  ["drone", "Drone coverage"],
  ["album", "Printed album"],
  ["live_stream", "Live stream"]
];

export function inquiryPayload(form) {
  return {
    clientName: form.clientName.trim(),
    email: form.email.trim(),
    phone: form.phone.trim(),
    eventTypeId: form.eventTypeId,
    ...(form.eventName.trim() ? { eventName: form.eventName.trim() } : {}),
    startsAt: eventLocalToUtc(form.startsAt, form.timezone),
    ...(form.endsAt ? { endsAt: eventLocalToUtc(form.endsAt, form.timezone) } : {}),
    timezone: form.timezone,
    venue: {
      ...(form.venueName.trim() ? { name: form.venueName.trim() } : {}),
      city: form.city.trim(),
      country: form.country
    },
    ...(form.guestCount ? { guestCount: Number(form.guestCount) } : {}),
    functionCount: Number(form.functionCount || 1),
    services: form.services,
    planCode: form.planCode,
    ...(form.message.trim() ? { message: form.message.trim() } : {})
  };
}

export function InquiryPage() {
  const [form, setForm] = useState(emptyForm);
  const [eventTypes, setEventTypes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [paymentReturn, setPaymentReturn] = useState(null);
  const isConfirmed = paymentReturn?.status === "paid" || paymentReturn?.status === "converted";
  const clientPanelUrl =
    typeof window !== "undefined"
      ? getPanelUrls(window.location).client || ""
      : "";

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([loadInquiryEventTypes(controller.signal), loadPublicPlans(controller.signal)])
      .then(([items, availablePlans]) => {
        setEventTypes(items);
        setPlans(availablePlans);
        setForm((current) => ({
          ...current,
          eventTypeId: current.eventTypeId || items[0]?.id || "",
          planCode: current.planCode || availablePlans[0]?.code || ""
        }));
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    const payment = parameters.get("plan_payment");
    const inquiryId = parameters.get("inquiry_id");
    if (!payment || !inquiryId) return;
    if (payment === "cancelled") {
      setPaymentReturn({ status: "cancelled", inquiryId });
      return;
    }
    const sessionId = parameters.get("session_id");
    if (payment !== "returned" || !sessionId) return;
    setBusy(true);
    setPaymentReturn({ status: "checking", inquiryId });
    reconcileInquiryPlanCheckout(inquiryId, sessionId)
      .then((data) => {
        setPaymentReturn({ inquiryId, ...data, status: "paid" });
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((requestError) => {
        setPaymentReturn({ status: "error", inquiryId });
        setError(requestError.message);
      })
      .finally(() => setBusy(false));
  }, []);

  function field(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function toggleService(code) {
    setForm((current) => ({
      ...current,
      services: current.services.includes(code)
        ? current.services.filter((item) => item !== code)
        : [...current.services, code]
    }));
  }

  async function continueToCheckout(inquiry) {
    try {
      const checkout = await startInquiryPlanCheckout(inquiry.id);
      window.location.assign(checkout.url);
      return true;
    } catch (requestError) {
      if (requestError.status === 401 || requestError.status === 403) {
        setNeedsVerification(true);
        return false;
      }
      throw requestError;
    }
  }

  async function sendVerificationCode() {
    setBusy(true);
    setError("");
    try {
      const result = await requestOtp(form.email.trim());
      setVerificationSent(true);
      setDevCode(result?.devCode || "");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifyAndCheckout(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyOtp(form.email.trim(), otpCode.trim(), "client");
      setNeedsVerification(false);
      await continueToCheckout(submitted);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function retryCheckout(inquiry = submitted) {
    if (!inquiry) return;
    setBusy(true);
    setError("");
    try {
      await continueToCheckout(inquiry);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function resumeReturnedCheckout() {
    setBusy(true);
    setError("");
    try {
      const checkout = await startInquiryPlanCheckout(paymentReturn.inquiryId);
      window.location.assign(checkout.url);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!form.services.length) {
      setError("Choose at least one service.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const inquiry = await submitEventInquiry(inquiryPayload(form));
      setSubmitted(inquiry);
      window.scrollTo?.({ top: 0, behavior: "smooth" });
      await continueToCheckout(inquiry);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-public-page px-inquiry-page">
      <header className="px-inquiry-header">
        <a className="px-site-brand" href="/" aria-label="PhotoX homepage">
          <span className="px-site-brand-mark"><Aperture size={21} /></span>
          <span>photo<span className="px-site-brand-x">x</span><small>STORIES, FOUND.</small></span>
        </a>
        <a className="px-site-header-back" href="/"><ArrowLeft size={16} /> Back to website</a>
      </header>
      <main className="px-inquiry-layout">
        <aside className="px-inquiry-story">
          <img
            src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?auto=format&fit=crop&w=1400&q=88"
            alt="A wedding celebration photographed in warm evening light"
          />
          <div className="px-inquiry-story-shade" />
          <div className="px-inquiry-story-copy">
            <span><Sparkles size={14} /> PHOTOGRAPHY, MATCHED TO YOUR MOMENT</span>
            <h1>Tell us what you’re planning.</h1>
            <p>Choose a fixed event plan, pay securely, and your event is created as soon as Stripe confirms payment.</p>
            <div className="px-inquiry-promises">
              <span><Check size={14} /> One fixed plan price</span>
              <span><Check size={14} /> 85% photographer / 15% PhotoX</span>
              <span><Check size={14} /> No quote or negotiation step</span>
            </div>
          </div>
        </aside>
        <section className="px-inquiry-content">
          {paymentReturn ? (
            <div className="px-inquiry-success" role="status">
              <span>{isConfirmed ? <CheckCircle2 size={31} /> : <LockKeyhole size={31} />}</span>
              <p className="px-site-overline">SECURE PLAN CHECKOUT</p>
              <h2>
                {isConfirmed
                  ? "Payment confirmed."
                  : paymentReturn.status === "checking"
                    ? "Confirming your payment…"
                    : paymentReturn.status === "cancelled"
                      ? "Checkout paused."
                      : "We could not confirm payment."}
              </h2>
              <p>
                {isConfirmed
                  ? "Your event has been created in the Client and Admin panels. Admin can assign a photographer, or approve a photographer who registers for it."
                  : paymentReturn.status === "cancelled"
                    ? "Your request is saved, but no event was created because payment was not completed."
                    : paymentReturn.status === "checking"
                      ? "Please keep this page open while Stripe confirms the transaction. The event is created only after confirmation."
                      : "No event was created. Retry checkout or contact PhotoX support if the problem continues."}
              </p>
              {error && <div className="px-inquiry-error" role="alert">{error}</div>}
              {isConfirmed && (
                <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                  <a
                    href={clientPanelUrl ? `${clientPanelUrl}/?view=events` : "/login?as=client"}
                    className="px-site-button px-site-button--large"
                  >
                    <CalendarDays size={18} /> View your event in Client Portal
                  </a>
                  <a href="/" className="px-site-button px-site-button--outline">
                    <ArrowLeft size={16} /> Return to homepage
                  </a>
                </div>
              )}
              {!isConfirmed && paymentReturn.status !== "checking" && (
                <button
                  type="button"
                  className="px-site-button px-site-button--large"
                  disabled={busy}
                  onClick={resumeReturnedCheckout}
                >
                  {busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />} Continue secure checkout
                </button>
              )}
            </div>
          ) : submitted ? (
            <div className="px-inquiry-success" role="status">
              <span><CheckCircle2 size={31} /></span>
              <p className="px-site-overline">REQUEST RECEIVED</p>
              <h2>{needsVerification ? "Verify your email to pay." : "Your request is ready."}</h2>
              <p>Your details and fixed-price plan are saved. Complete secure checkout to create the event.</p>
              <p>{submitted.confirmationEmailSent ? `A confirmation email has been sent to ${form.email}.` : "Your request is saved, but its confirmation email could not be delivered. Keep the reference below and contact PhotoX support if needed."}</p>
              <dl>
                <div><dt>Reference</dt><dd>{submitted.reference}</dd></div>
                <div><dt>Status</dt><dd>Awaiting payment</dd></div>
                <div><dt>Selected plan</dt><dd>{plans.find((plan) => plan.code === form.planCode)?.name || form.planCode}</dd></div>
                <div><dt>What happens next</dt><dd>Payment creates the event, then Admin assigns a photographer or approves a registration.</dd></div>
              </dl>
              {error && <div className="px-inquiry-error" role="alert">{error}</div>}
              {needsVerification ? (
                verificationSent ? (
                  <form className="px-inquiry-verification" onSubmit={verifyAndCheckout}>
                    <label>Verification code<input required inputMode="numeric" autoComplete="one-time-code" value={otpCode} onChange={(event) => setOtpCode(event.target.value)} placeholder="Enter the code sent to your email" /></label>
                    {showDevOtpFill(devCode) && (
                      <button
                        type="button"
                        className="px-site-dev-code"
                        onClick={() => setOtpCode(devCode)}
                        title="Tap to fill the same OTP emailed by SES (testing only)"
                      >
                        Testing OTP: <strong>{devCode}</strong> <span>Tap to fill</span>
                      </button>
                    )}
                    <button className="px-site-button px-site-button--large" disabled={busy || otpCode.trim().length < 4}>{busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />} Verify & continue to Stripe</button>
                  </form>
                ) : (
                  <button type="button" className="px-site-button px-site-button--large" disabled={busy} onClick={sendVerificationCode}>{busy ? <LoaderCircle className="spin" size={18} /> : <LockKeyhole size={18} />} Send secure code to {form.email}</button>
                )
              ) : (
                <button type="button" className="px-site-button px-site-button--large" disabled={busy} onClick={() => retryCheckout()}>{busy ? <LoaderCircle className="spin" size={18} /> : <ArrowRight size={18} />} Continue directly to Stripe</button>
              )}
            </div>
          ) : (
            <form className="px-inquiry-form" onSubmit={submit}>
              <div className="px-inquiry-form-head">
                <p className="px-site-overline">REQUEST A PHOTOGRAPHER</p>
                <h2>Start with your event.</h2>
                <p>The selected plan is the final event price, including photographer service. There is no later quote or negotiation.</p>
              </div>
              {error && <div className="px-inquiry-error" role="alert">{error}</div>}
              {loading ? (
                <div className="px-inquiry-loading"><LoaderCircle className="spin" /> Loading event options…</div>
              ) : (
                <>
                  <fieldset>
                    <legend><span>01</span> About you</legend>
                    <div className="px-inquiry-grid">
                      <Field label="Your name" value={form.clientName} onChange={(value) => field("clientName", value)} autoComplete="name" />
                      <Field label="Email address" type="email" value={form.email} onChange={(value) => field("email", value)} autoComplete="email" />
                      <Field label="Phone number" type="tel" value={form.phone} onChange={(value) => field("phone", value)} autoComplete="tel" />
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend><span>02</span> Event details</legend>
                    <div className="px-inquiry-grid">
                      <label>Event type<select required value={form.eventTypeId} onChange={(event) => field("eventTypeId", event.target.value)}><option value="">Select event type</option>{eventTypes.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select></label>
                      <Field label="Event name (optional)" value={form.eventName} onChange={(value) => field("eventName", value)} required={false} placeholder="Riya & Arjun’s wedding" />
                      <Field label="Start date and time" type="datetime-local" value={form.startsAt} onChange={(value) => field("startsAt", value)} />
                      <Field label="End date and time (optional)" type="datetime-local" value={form.endsAt} onChange={(value) => field("endsAt", value)} required={false} />
                      <label>Country<select value={form.country} onChange={(event) => setForm((current) => ({ ...current, country: event.target.value, timezone: event.target.value === "India" ? "Asia/Kolkata" : event.target.value === "United States" ? "America/New_York" : "America/Toronto" }))}><option value="Canada">Canada</option><option value="United States">United States</option><option value="India">India</option><option value="Other">Other</option></select></label>
                      <label>Event timezone<select value={form.timezone} onChange={(event) => field("timezone", event.target.value)}><option value="America/Toronto">Eastern (Toronto / New York)</option><option value="America/Chicago">Central (Chicago)</option><option value="America/Winnipeg">Central (Winnipeg)</option><option value="America/Denver">Mountain (Denver)</option><option value="America/Edmonton">Mountain (Edmonton)</option><option value="America/Vancouver">Pacific (Vancouver / Los Angeles)</option><option value="America/Halifax">Atlantic (Halifax)</option><option value="America/St_Johns">Newfoundland</option><option value="Asia/Kolkata">India (IST)</option><option value="UTC">UTC / Other</option></select></label>
                      <Field label="City" value={form.city} onChange={(value) => field("city", value)} autoComplete="address-level2" />
                      <Field label="Venue (optional)" value={form.venueName} onChange={(value) => field("venueName", value)} required={false} />
                      <Field label="Number of functions" type="number" value={form.functionCount} onChange={(value) => field("functionCount", value)} min="1" max="100" />
                      <Field label="Estimated guests (optional)" type="number" value={form.guestCount} onChange={(value) => field("guestCount", value)} required={false} min="1" max="100000" />
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend><span>03</span> Coverage you need</legend>
                    <div className="px-inquiry-services">{services.map(([code, label]) => <label key={code} className={form.services.includes(code) ? "selected" : ""}><input type="checkbox" checked={form.services.includes(code)} onChange={() => toggleService(code)} /><Camera size={17} />{label}</label>)}</div>
                    <label>Anything else we should know?<textarea value={form.message} maxLength="2000" placeholder="Tell us about the schedule, style or special moments you want covered." onChange={(event) => field("message", event.target.value)} /></label>
                  </fieldset>
                  <fieldset>
                    <legend><span>04</span> Choose your event plan</legend>
                    <p className="px-inquiry-plan-intro">These live catalogue prices are fixed. The assigned photographer receives 85% and PhotoX retains a 15% commission.</p>
                    <div className="px-inquiry-plans">
                      {plans.map((plan) => {
                        const selected = form.planCode === plan.code;
                        const storageBytes = plan.entitlements?.storageBytes ?? plan.storageBytes ?? 0;
                        const storageGb = Math.round(storageBytes / 1073741824);
                        return <label key={plan.code} className={selected ? "selected" : ""}>
                          <input type="radio" name="planCode" value={plan.code} checked={selected} onChange={() => field("planCode", plan.code)} />
                          <span className="px-inquiry-plan-check"><Check size={15} /></span>
                          <strong>{plan.name}</strong>
                          {Number.isInteger(plan.amountMinor) ? <b>{new Intl.NumberFormat("en-IN", { style: "currency", currency: plan.currency || "INR" }).format(plan.amountMinor / 100)} <small>/ {plan.billingInterval === "event" ? "event" : plan.billingInterval}</small></b> : <b>Price confirmed at checkout</b>}
                          <em>{storageGb} GB storage · {plan.entitlements?.maxPhotos ? `${plan.entitlements.maxPhotos.toLocaleString()} photos` : "Flexible photo count"}</em>
                          <small>{plan.entitlements?.retentionDays || 90} days retention{plan.entitlements?.faceSearch ? " · AI face search" : ""}</small>
                        </label>;
                      })}
                    </div>
                    {!plans.length && <div className="px-inquiry-error">No subscription plans are currently published. Please contact PhotoX support.</div>}
                  </fieldset>
                  <div className="px-inquiry-submit-row">
                    <p><LockKeyhole size={15} /> Your details are used only to prepare and manage this booking.</p>
                    <button className="px-site-button px-site-button--large" disabled={busy || !eventTypes.length || !form.planCode}>{busy ? <LoaderCircle className="spin" size={18} /> : <CalendarDays size={18} />}{busy ? "Preparing checkout…" : "Save request & continue to payment"}</button>
                  </div>
                </>
              )}
            </form>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required = true, ...inputProps }) {
  return <label>{label}<input required={required} type={type} value={value} onChange={(event) => onChange(event.target.value)} {...inputProps} /></label>;
}
